import {
	AmcInfos,
	DiagnosticSelAmc,
	ErrorGroupe,
	ReponseSelAmc,
	ReponseSelAmcAction,
	ReponseSelAmcParsed,
	TypeErreurSelAmc,
	TypeService,
	UrlAmc,
} from "@jack-sparrow-libraries/emotion-models";
import { inject, Injectable } from "@angular/core";
import { environment } from "@env/environment";
import { SelamcAlertService } from "@featuresAY/shared/garantie-paiement/tlsi/tlsi-selamc/services/selamc-alert.service";
import { UiKitToasterService } from "@jack-sparrow-libraries/ui-kit";

@Injectable({
	providedIn: "root",
})
export class SelamcReponseService {
	private readonly selamcAlertService = inject(SelamcAlertService);
	private readonly toastrAyService = inject(UiKitToasterService);

	private readonly SERVICE_FTP = "FTP";
	private readonly SERVICE_HTP = "HTP";
	private readonly SERVICE_IDB = "IDB";
	private readonly DIAGNOSTIC_CODE_ULR = "URL";
	private readonly DIAGNOSTIC_CODE_SELECTURL = "SelectURL";

	//Traite les retours du WS appelIdb
	async traiteReponseSelAmc(
		reponseSelAmc: ReponseSelAmc,
		erreurTechBloquante = true,
		missingUrlCounter = 0,
	): Promise<ReponseSelAmcParsed> {
		const response: ReponseSelAmcParsed = this.parseReponseSelAmc(reponseSelAmc);

		// Si on a un diagnostic en retour
		if (response?.diagnosticSelAmc?.errorsGroupe) {
			//Erreur technique silencieuse
			if (this.isErreurTechniqueSilencieuse(response.diagnosticSelAmc.errorsGroupe, erreurTechBloquante)) {
				return {
					returnAction: ReponseSelAmcAction.ERROR,
					typeErreur: TypeErreurSelAmc.TechniqueNonBloquantSilencieux,
					returnMessage: response.diagnosticSelAmc.errorsGroupe,
					diagnosticSelAmc: response.diagnosticSelAmc,
				};
			}
			//erreur technique depuis l'ordo uniquement - maintenance
			else if (
				response.diagnosticSelAmc.errorsGroupe === ErrorGroupe.ERREUR_TECHNIQUE_WS &&
				erreurTechBloquante
			) {
				return {
					returnAction: ReponseSelAmcAction.ERROR,
					typeErreur: TypeErreurSelAmc.Technique,
					returnMessage: response.diagnosticSelAmc.errorsGroupe,
					diagnosticSelAmc: response.diagnosticSelAmc,
				};

				//sinon erreur fonctionnelle
			} else {
				return {
					returnAction: ReponseSelAmcAction.ERROR,
					typeErreur: TypeErreurSelAmc.Fonctionnelle,
					diagnosticSelAmc: response.diagnosticSelAmc,
				};
			}
		}

		// @JM REV-5375 On masque les erreurs URL_MISSING .. il faudrait faire un appel à l'annuaire mais ce n'est pas gérer coté mobile
		if (response.returnAction === ReponseSelAmcAction.URL_MISSING) {
			if (missingUrlCounter > 1) {
				return {
					returnAction: ReponseSelAmcAction.ERROR,
					typeErreur: TypeErreurSelAmc.Technique, // maintenance
					returnMessage: response.diagnosticSelAmc.errorsGroupe,
					diagnosticSelAmc: response.diagnosticSelAmc,
				};
			}
			return {
				returnAction: ReponseSelAmcAction.URL_MISSING,
				typeErreur: TypeErreurSelAmc.Fonctionnelle,
				returnMessage: response.diagnosticSelAmc.errorsGroupe,
				diagnosticSelAmc: response.diagnosticSelAmc,
				amcJson: response.amcJson,
				versionAgreeAnnuaireAmc: response.versionAgreeAnnuaireAmc,
			};
		} else if (response.returnAction === ReponseSelAmcAction.SERVICE_SUIVANT_IDB) {
			return {
				returnAction: ReponseSelAmcAction.ERROR,
				typeErreur: TypeErreurSelAmc.Fonctionnelle,
				diagnosticSelAmc: { errorsGroupe: ErrorGroupe.BENEFICIAIRE_NON_CONNU_DE_L_AMC },
			};
		} else if (response.returnAction === ReponseSelAmcAction.URL_SELECT) {
			const responseUrl = await this.selamcAlertService.showUrlSelectDialog(response.listeUrlAmcJson);

			if (responseUrl) {
				const urlAmc = this.buildUrlAmc(responseUrl);
				return {
					returnAction: ReponseSelAmcAction.URL_SELECT,
					amcJson: urlAmc,
				};
			}
			this.toastrAyService.show({
				color: "alert",
				titre: "Le service de contrôle des droits mutuelle a retourné le message suivant",
				subtitle:
					"Le choix du type de convention et/ou du critère secondaire est nécessaire pour utiliser la mutuelle",
			});

			return { returnAction: ReponseSelAmcAction.ERROR, typeErreur: TypeErreurSelAmc.Abandon };
		} else if (
			response.returnAction === ReponseSelAmcAction.SUCCESS ||
			response.returnAction === ReponseSelAmcAction.NEW_ATTESTATION
		) {
			return response;
		}

		return {
			returnAction: ReponseSelAmcAction.ERROR,
			typeErreur: TypeErreurSelAmc.TechniqueNonBloquant,
			returnMessage: "Erreur inconnue",
		};
	}

	public buildUrlAmc(responseUrl: UrlAmc) {
		return {
			url: responseUrl.url,
			service: responseUrl.service,
			amc: responseUrl.amc,
			typeConv: responseUrl.typeConv,
			csr: responseUrl.csr,
			csrLibelle: responseUrl.csrLibelle,
			commentaire: responseUrl.commentaire,
			contactNom: responseUrl.contactNom,
			contactPrenom: responseUrl.contactPrenom,
			contactMail: responseUrl.contactMail,
			contactTel: responseUrl.contactTel,
			domaineConventionnel: responseUrl.domaineConventionnel,
			codeService: responseUrl.codeService,
			codeNorme: responseUrl.codeNorme,
			versionNorme: responseUrl.versionNorme,
			typeAdresse: responseUrl.typeAdresse,
			dureeValidite: responseUrl.dureeValidite,
			dateReponse: responseUrl.dateReponse,
			reseauLibelle: responseUrl.reseauLibelle,
		};
	}

	private isErreurTechniqueSilencieuse(err: ErrorGroupe, appelFromOrdo: boolean): boolean {
		if (
			err === ErrorGroupe.DONNEE_INCORRECTE ||
			err === ErrorGroupe.ERREUR_TECHNIQUE_EMOTION ||
			(err === ErrorGroupe.ERREUR_TECHNIQUE_WS && !appelFromOrdo) ||
			err === ErrorGroupe.ACCES_AU_WS_REFUSE
		) {
			return true;
		}
		return false;
	}

	//On traite uniquement les retours IDB - faudra voir pour les appels CLC en facturation
	public parseReponseSelAmc(reponseSelAmc: ReponseSelAmc): ReponseSelAmcParsed {
		//si service suivant
		if (reponseSelAmc.serviceSuivant) {
			return this.traiteServiceSuivant(reponseSelAmc);
		}

		//Traite Diagnostic en priorité -- si on a un diag c'est qu'on a une erreur en retour
		if (reponseSelAmc.diagnostic) {
			return this.traiteDiagnostic(reponseSelAmc);
		}
		//Sinon traite retour idb -- on peut aussi avoir une erreur sans diag. on n'a pas pu faire la demande
		if (reponseSelAmc.type === TypeService.Idb) {
			return this.traiteRetourIdb(reponseSelAmc);
		} else if (reponseSelAmc.type === TypeService.Ftp || reponseSelAmc.type === TypeService.Htp) {
			return this.traiteRetourFtpHtp(reponseSelAmc);
		}

		//Sinon erreur
		return { returnAction: ReponseSelAmcAction.ERROR };
	}

	private traiteServiceSuivant(reponseSelAmc: ReponseSelAmc): ReponseSelAmcParsed {
		const serviceSuivant: string = reponseSelAmc.serviceSuivant;

		// Cas ou il faut refaire un appel - changement de mutuelle
		if (serviceSuivant === this.SERVICE_IDB && reponseSelAmc.type === TypeService.Idb) {
			return {
				returnAction: ReponseSelAmcAction.SERVICE_SUIVANT_IDB,
				amcJson: reponseSelAmc.urlAmc
					? reponseSelAmc.urlAmc
					: reponseSelAmc.urlAmcs && reponseSelAmc.urlAmcs.length > 0
						? reponseSelAmc.urlAmcs[0]
						: null,
			};
		}

		if (
			serviceSuivant === this.SERVICE_FTP ||
			(serviceSuivant === this.SERVICE_HTP &&
				(reponseSelAmc.type === TypeService.Ftp || reponseSelAmc.type === TypeService.Htp))
		) {
			return {
				returnAction: ReponseSelAmcAction.SERVICE_SUIVANT_FTP,
				amcJson: reponseSelAmc.urlAmc
					? reponseSelAmc.urlAmc
					: reponseSelAmc.urlAmcs && reponseSelAmc.urlAmcs.length > 0
						? reponseSelAmc.urlAmcs[0]
						: null,
			};
		}
		return {
			returnAction: ReponseSelAmcAction.ERROR,
			returnMessage: `Service suivant inconnu : ${serviceSuivant}`,
		};
	}

	private traiteDiagnostic(reponseSelAmc: ReponseSelAmc): ReponseSelAmcParsed {
		const diagnosticSelAmc: DiagnosticSelAmc = reponseSelAmc.diagnostic;

		let returnAction: ReponseSelAmcAction;
		let returnMessage: string;
		let versionAgreeAnnuaireAmc: string;
		let amcJson: UrlAmc;
		let listeUrlAmcJson: UrlAmc[];
		if (diagnosticSelAmc.code && diagnosticSelAmc.code !== "") {
			returnAction = ReponseSelAmcAction.ERROR_FONC;
			returnMessage = `Message du Service En Ligne AMC (IDB) : ${diagnosticSelAmc.libelle}`;

			if (diagnosticSelAmc.code === this.DIAGNOSTIC_CODE_ULR) {
				amcJson = reponseSelAmc.urlAmc
					? reponseSelAmc.urlAmc
					: reponseSelAmc.urlAmcs && reponseSelAmc.urlAmcs.length > 0
						? reponseSelAmc.urlAmcs[0]
						: null;
				versionAgreeAnnuaireAmc = environment.SITE_VERSION_EMOTION_AGREMENT_INTER_AMC;
				returnAction = ReponseSelAmcAction.URL_MISSING;
			} else if (diagnosticSelAmc.code === this.DIAGNOSTIC_CODE_SELECTURL) {
				listeUrlAmcJson = reponseSelAmc.urlAmcs;
				returnAction = ReponseSelAmcAction.URL_SELECT;
			}
		} else {
			returnAction = ReponseSelAmcAction.ERROR;
			returnMessage = diagnosticSelAmc.libelle;
		}

		return {
			returnAction,
			returnMessage,
			versionAgreeAnnuaireAmc,
			amcJson,
			listeUrlAmcJson,
			diagnosticSelAmc,
		};
	}

	private traiteRetourIdb(reponseSelAmcIdb: ReponseSelAmc): ReponseSelAmcParsed {
		const amcInfosFromDemande: AmcInfos = reponseSelAmcIdb.amcInfosFromDemande;
		if (amcInfosFromDemande == null) {
			return {
				returnAction: ReponseSelAmcAction.ERROR,
				returnMessage: "Les informations AMC de la réponse ne sont pas renseignées",
			};
		}

		if (reponseSelAmcIdb.amcInfosFromDemande.mutuelleAttestationId) {
			return { returnAction: ReponseSelAmcAction.SUCCESS };
		}
		return { returnAction: ReponseSelAmcAction.NEW_ATTESTATION };
	}

	private traiteRetourFtpHtp(reponseSelAmcFtp: ReponseSelAmc): ReponseSelAmcParsed {
		if (reponseSelAmcFtp.urlAmc) {
			return {
				returnAction: ReponseSelAmcAction.SUCCESS,
				amcJson: reponseSelAmcFtp.urlAmc,
				urlAmcHTP: reponseSelAmcFtp.urlAmcHTP,
			};
		}

		return {
			returnAction: ReponseSelAmcAction.ERROR,
			returnMessage: "Les informations AMC de la réponse ne sont pas renseignées",
		};
	}
}
